import { useState, useRef, useLayoutEffect } from "react"
import { useTranslation } from "react-i18next"

interface ExpandableTextProps {
  text: string
  className?: string
  lineClamp?: number
}

export function ExpandableText({
  text,
  className = "",
  lineClamp = 2,
}: ExpandableTextProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  // 접힌 상태에서만 측정한다: 펼친 상태에서는 clamp가 없어 항상 "넘치지 않음"으로 나오므로,
  // 그 값을 남기면 다시 접었을 때 "더보기"가 사라진다. 접으면(isExpanded 변경) 다시 측정.
  useLayoutEffect(() => {
    const el = textRef.current
    if (!el || isExpanded) return

    const checkClamped = () => setIsClamped(el.scrollHeight > el.clientHeight)
    checkClamped()

    const observer = new ResizeObserver(checkClamped)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, isExpanded])

  const lineClampClass = `line-clamp-${lineClamp}`

  return (
    <div className="relative">
      <p
        ref={textRef}
        className={`${className} ${!isExpanded ? lineClampClass : ""}`}
      >
        {text}
      </p>
      {isExpanded ? (
        <div className="flex justify-end mt-1">
          <button
            onClick={() => setIsExpanded(false)}
            aria-expanded
            className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
          >
            {t('pages.showLess')}
          </button>
        </div>
      ) : (
        isClamped && (
          <button
            onClick={() => setIsExpanded(true)}
            aria-expanded={false}
            className="absolute bottom-0 right-0 text-xs text-slate-500 hover:text-slate-700 transition-colors bg-gradient-to-l from-white from-60% to-transparent pl-10 py-0.5"
          >
            {t('pages.showMore')}
          </button>
        )
      )}
    </div>
  )
}
